import React, { createContext, useContext, useReducer, useCallback, useEffect, useRef } from 'react';
import {
  ConversationState,
  ConversationAction,
  Message,
  ConversationConfig,
  ProductActionType,
  ProductActionSourceProduct,
  ProductActionResponse,
} from '../types/conversation';
import { useWebSocket } from '../hooks/useWebSocketFixed';
import { useAudioRecorder } from '../hooks/useAudioRecorder';
import { useAppConfig } from './ConfigContext';
import { useErrorMessages } from '../hooks/useErrorMessages';
import { useSmartRetry } from '../hooks/useSmartRetry';
import { DEFAULT_RETRY_CONFIG } from '../config/retryConfig';
import appConfig from '../config';

// Get configuration from environment variables
const getConfig = (): ConversationConfig => ({
  apiUrl: appConfig.apiUrl,
  websocketUrl: appConfig.websocketUrl,
  maxRecordingDuration: parseInt(process.env.REACT_APP_MAX_RECORDING_DURATION || '30'),
  audioConfig: {
    sampleRate: parseInt(process.env.REACT_APP_AUDIO_SAMPLE_RATE || '16000'),
    channels: parseInt(process.env.REACT_APP_AUDIO_CHANNELS || '1'),
    bitsPerSample: parseInt(process.env.REACT_APP_BITS_PER_SAMPLE || '16'),
  },
});

const initialState: ConversationState = {
  messages: [],
  isConnected: false,
  isRecording: false,
  isProcessing: false,
  isAudioPlaying: false,
  currentlyPlayingMessageId: null,
  error: null,
  connectionStatus: 'disconnected',
  conversationId: null,
};

// Storage keys for sessionStorage
const STORAGE_KEY = 'conversation-history';
const CONVERSATION_ID_KEY = 'conversation-id';

// Generate a new conversation ID
const generateConversationId = (): string => {
  return `conv_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};



// Serialize messages for storage (handle Date objects and exclude large audio data)
const serializeMessages = (messages: Message[]): string => {
  const messagesToStore = messages.map(msg => ({
    id: msg.id,
    type: msg.type,
    content: msg.content,
    timestamp: msg.timestamp.toISOString(),
    messageType: msg.messageType,
    hasAudio: msg.hasAudio,
    duration: msg.duration,
    conversationId: msg.conversationId,
    // Include images for restoration
    ...(msg.images && msg.images.length > 0 && { images: msg.images }),
    // Exclude audio data, audioUrl, and temporary states to save space
    // audioData, audioUrl, isStreaming, isGeneratingTTS, shouldAutoPlay excluded
  }));
  return JSON.stringify(messagesToStore);
};

// Deserialize messages from storage (restore Date objects)
const deserializeMessages = (data: string): Message[] => {
  try {
    const parsed = JSON.parse(data);
    return parsed.map((msg: any) => ({
      ...msg,
      timestamp: new Date(msg.timestamp),
      conversationId: msg.conversationId,
      // Preserve images if they exist
      images: msg.images || undefined,
      // Reset temporary states
      isStreaming: false,
      isGeneratingTTS: false,
      shouldAutoPlay: false,
    }));
  } catch (error) {
    console.error('Failed to deserialize messages:', error);
    return [];
  }
};

function conversationReducer(state: ConversationState, action: ConversationAction): ConversationState {
  switch (action.type) {
    case 'ADD_MESSAGE':
      return {
        ...state,
        messages: [...state.messages, action.payload],
      };      case 'UPDATE_MESSAGE':
      // Check if message exists
      const messageExists = state.messages.some(msg => msg.id === action.payload.id);
      
      if (messageExists) {
        // Check if the update would actually change anything
        const existingMessage = state.messages.find(msg => msg.id === action.payload.id);
        const updates = action.payload.updates;
        
        // Skip update if content and streaming state haven't changed
        if (existingMessage && 
            existingMessage.content === updates.content && 
            existingMessage.isStreaming === updates.isStreaming) {
          return state; // No change needed
        }
        
        // Update existing message
        return {
          ...state,
          messages: state.messages.map(msg => 
            msg.id === action.payload.id 
              ? { ...msg, ...action.payload.updates }
              : msg
          ),
        };
      } else {
        // Create new streaming message if it doesn't exist
        const newStreamingMessage: Message = {
          id: action.payload.id,
          type: 'assistant',
          content: action.payload.updates.content || '',
          timestamp: new Date(),
          messageType: 'text',
          isStreaming: action.payload.updates.isStreaming ?? false,
          ...action.payload.updates
        };
        return {
          ...state,
          messages: [...state.messages, newStreamingMessage],
        };
      }
    
    case 'UPDATE_LAST_AUDIO_MESSAGE':
      // Find the last user audio message that is streaming and update it
      const lastAudioIndex = state.messages.map((msg, index) => ({ msg, index }))
        .filter(({ msg }) => msg.type === 'user' && msg.messageType === 'audio' && msg.isStreaming)
        .pop()?.index;
      
      if (lastAudioIndex !== undefined) {
        return {
          ...state,
          messages: state.messages.map((msg, index) => 
            index === lastAudioIndex
              ? { ...msg, content: action.payload.transcription, isStreaming: false }
              : msg
          ),
        };
      }
      return state;
    
    case 'UPDATE_TRANSCRIPTION':
      // Update specific message with transcription
      return {
        ...state,
        messages: state.messages.map(msg => 
          msg.id === action.payload.messageId
            ? { ...msg, content: action.payload.transcription, isStreaming: false }
            : msg
        ),
      };      case 'UPDATE_AUDIO_RESPONSE':
      // Update specific message with audio data and trigger auto-play
      return {
        ...state,
        messages: state.messages.map(msg => 
          msg.id === action.payload.messageId
            ? { 
                ...msg, 
                audioData: action.payload.audioData, 
                hasAudio: true,
                audioUrl: `data:audio/mp3;base64,${action.payload.audioData}`,
                isGeneratingTTS: false, // Clear TTS processing when audio arrives
                // Keep shouldAutoPlay flag - it will be used by AudioPlayer
              }
            : msg
        ),
      };    case 'SET_TTS_PROCESSING':
      // Set TTS processing state for a specific message
      return {
        ...state,
        messages: state.messages.map(msg => 
          msg.id === action.payload.messageId
            ? { 
                ...msg, 
                isGeneratingTTS: action.payload.isProcessing,
                shouldAutoPlay: action.payload.isProcessing // Set autoplay flag when TTS starts
              }
            : msg
        ),
      };
    
    case 'CLEAR_AUTOPLAY':
      // Clear the autoplay flag after audio has been processed
      return {
        ...state,
        messages: state.messages.map(msg => 
          msg.id === action.payload.messageId
            ? { ...msg, shouldAutoPlay: false }
            : msg
        ),
      };
      case 'SET_CONNECTION_STATUS':
      return {
        ...state,
        connectionStatus: action.payload,
        isConnected: action.payload === 'connected',
        // Clear error when successfully connected
        error: action.payload === 'connected' ? null : state.error,
      };
    
    case 'SET_RECORDING':
      return {
        ...state,
        isRecording: action.payload,
      };
      case 'SET_PROCESSING':
      return {
        ...state,
        isProcessing: action.payload,
      };
      case 'SET_AUDIO_PLAYING':
      return {
        ...state,
        isAudioPlaying: action.payload,
      };
    
    case 'SET_CURRENTLY_PLAYING_MESSAGE':
      return {
        ...state,
        isAudioPlaying: action.payload.isPlaying,
        currentlyPlayingMessageId: action.payload.messageId,
      };
    
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
      };    case 'CLEAR_MESSAGES':
      return {
        ...state,
        messages: [],
        isAudioPlaying: false, // Reset audio playing state
        isProcessing: false,   // Reset processing state
        error: null,          // Clear any errors
        // Keep conversationId to maintain session tracking
      };
    
    case 'LOAD_MESSAGES':
      return {
        ...state,
        messages: action.payload,
        isAudioPlaying: false, // Reset audio playing state
        isProcessing: false,   // Reset processing state
        error: null,          // Clear any errors
      };
    
    case 'START_NEW_CONVERSATION':
      return {
        ...state,
        messages: [],
        conversationId: action.payload.conversationId,
        isAudioPlaying: false,
        isProcessing: false,
        error: null,
      };
    
    case 'SET_CONVERSATION_ID':
      return {
        ...state,
        conversationId: action.payload,
      };
    
    case 'RESTORE_CONVERSATION':
      return {
        ...state,
        conversationId: action.payload.conversationId,
        messages: action.payload.messages,
        isAudioPlaying: false,
        isProcessing: false,
        error: null,
      };
    
    case 'MODERATION_BLOCKED':
      // Add an assistant message showing the moderation response
      const moderationMessage: Message = {
        id: `moderation_${action.payload.messageId}`,
        type: 'assistant',
        content: action.payload.content,
        timestamp: new Date(),
        moderated: true,
        isStreaming: false,
      };
      return {
        ...state,
        messages: [...state.messages, moderationMessage],
        isProcessing: false,
      };
    
    default:
      return state;
  }
}

interface ConversationContextType extends ConversationState {
  config: ConversationConfig;
  sendMessage: (content: string) => void;
  sendVoiceMessage: (audioBlob: Blob) => void;
  sendImageSearch: (imageData: string, caption?: string, config?: any) => void;
  sendTTSRequest: (content: string, messageId?: string) => void;
  sendCitationClick: (citationUrl: string, citationTitle: string, citationIndex?: number, messageId?: string) => void;
  sendCopyClick: (copiedContent: string, messageId?: string) => void;
  sendProductActionTracking: (trackingEvent: 'impression' | 'button_click' | 'product_click' | 'add_to_cart' | 'quickview_open' | 'quickview_close', actionType: string, productUrl: string, productTitle?: string, messageId?: string, productPrice?: string, productPriceNumeric?: number) => void;
  sendFeedback: (messageId: string, feedbackType: 'positive' | 'negative', feedbackReasons?: string[], feedbackComment?: string, flyoutBypassed?: boolean) => void;
  sendProductAction: (action: ProductActionType, sourceProduct: ProductActionSourceProduct, onResponse?: (response: ProductActionResponse) => void) => void;
  startRecording: () => void;
  stopRecording: () => void;
  clearMessages: () => void;
  startNewConversation: () => void;
  connect: () => void;
  disconnect: () => void;
  retryConnection: () => void;
  setAudioPlaying: (isPlaying: boolean) => void;
  setCurrentlyPlayingMessage: (messageId: string | null, isPlaying: boolean) => void;
  clearAutoPlay: (messageId: string) => void;
  // Retry state
  isRetrying: boolean;
  retryAttemptCount: number;
  nextRetryIn: number;
  canRetry: boolean;
  // Error handling
  friendlyError: string | null;
}

const ConversationContext = createContext<ConversationContextType | null>(null);

export const useConversation = () => {
  const context = useContext(ConversationContext);
  if (!context) {
    throw new Error('useConversation must be used within a ConversationProvider');
  }
  return context;
};

export const ConversationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(conversationReducer, initialState);
  const config = getConfig();
  const { isConfigReady } = useAppConfig();
    // Error message handling
  const { getFriendlyError } = useErrorMessages();
  
  // Track pending message to retry after restoration
  const pendingRetryMessageRef = useRef<any | null>(null);
  
  // Connection retry functionality
  const connectionRetryFunction = useCallback(async (): Promise<boolean> => {
    try {
      // Test connection by attempting to establish WebSocket
      const testWs = new WebSocket(config.websocketUrl);
      
      return new Promise((resolve) => {
        const timeout = setTimeout(() => {
          testWs.close();
          resolve(false);
        }, 5000); // 5 second timeout
        
        testWs.onopen = () => {
          clearTimeout(timeout);
          testWs.close();
          resolve(true);
        };
        
        testWs.onerror = () => {
          clearTimeout(timeout);
          testWs.close();
          resolve(false);
        };
      });
    } catch (error) {
      console.error('Connection test failed:', error);
      return false;
    }
  }, [config.websocketUrl]);
  
  const retryHook = useSmartRetry(connectionRetryFunction, DEFAULT_RETRY_CONFIG.network);
  
  // Track restoration attempts to prevent multiple restoration calls
  const restorationAttemptedRef = useRef(false);
  
// Message handlers
  const onMessage = useCallback((rawMessage: any) => {
    console.log('Received WebSocket message:', rawMessage);
    
    // Handle different message types from the backend
    if (rawMessage.type === 'response') {
      // Check if this response includes user transcription metadata
      if (rawMessage.metadata && rawMessage.metadata.transcription) {
        // Update the last processing audio message with transcription
        dispatch({ 
          type: 'UPDATE_LAST_AUDIO_MESSAGE', 
          payload: { transcription: rawMessage.metadata.transcription }
        });
      }      // Create assistant response message
      const message: Message = {
        id: rawMessage.id || Date.now().toString(),
        content: rawMessage.content,
        type: 'assistant',
        timestamp: new Date(rawMessage.timestamp || Date.now()),
        audioData: rawMessage.audio_data,
        hasAudio: rawMessage.has_audio || false,
        messageType: rawMessage.has_audio ? 'tts' : 'text',
        isStreaming: false, // Mark as complete response
        isGeneratingTTS: rawMessage.is_generating_tts || false, // Set TTS generation state from backend
        conversationId: state.conversationId || undefined,
        images: rawMessage.images || undefined // Add images from RAG response
      };
      dispatch({ type: 'ADD_MESSAGE', payload: message });
      dispatch({ type: 'SET_PROCESSING', payload: false });
    } else if (rawMessage.type === 'transcription_update') {
      // Handle intermediate transcription updates
      console.log('Received transcription update:', rawMessage.transcription);
      dispatch({ 
        type: 'UPDATE_TRANSCRIPTION', 
        payload: { 
          messageId: rawMessage.message_id, 
          transcription: rawMessage.transcription 
        }
      });    } else if (rawMessage.type === 'audio_response') {
      // Handle intermediate audio response updates
      console.log('Received audio response update for message:', rawMessage.message_id);
      dispatch({ 
        type: 'UPDATE_AUDIO_RESPONSE', 
        payload: { 
          messageId: rawMessage.message_id, 
          audioData: rawMessage.audio_data 
        }
      });
      // Clear any processing state since audio generation is complete
      dispatch({ type: 'SET_PROCESSING', payload: false });
    } else if (rawMessage.type === 'image_keywords') {
      // Handle image keywords update from backend
      console.log('Received image keywords for message:', rawMessage.messageId);
      dispatch({
        type: 'UPDATE_MESSAGE',
        payload: {
          id: rawMessage.messageId,
          updates: {
            imageKeywords: rawMessage.keywords, // Store keywords for search context
            isStreaming: false // Mark as done processing, but keep original content/caption
          }
        }
      });
    } else if (rawMessage.type === 'stream') {
      // Handle streaming text responses
      const existingMessageId = rawMessage.id;
      if (existingMessageId) {
        // Check if we already have a message with this ID
        // If not, create new message; if yes, update existing
        const updates: Partial<Message> = {
          content: rawMessage.content,
          isStreaming: !rawMessage.complete
        };
        
        // Add images if this is the first chunk (usually empty content with images)
        if (rawMessage.images && rawMessage.images.length > 0) {
          updates.images = rawMessage.images;
        }
        
        // Tag product action responses using the ref set by sendProductAction
        if (pendingProductActionRef.current) {
          updates.messageType = 'product_action';
          pendingProductActionRef.current = false;
        }
        
        // Try to update existing message first
        dispatch({ type: 'UPDATE_MESSAGE', payload: { id: existingMessageId, updates } });
        
        // Clear processing state only when streaming completes
        if (rawMessage.complete) {
          dispatch({ type: 'SET_PROCESSING', payload: false });
        }
      }    } else if (rawMessage.type === 'status') {
      console.log('Status message:', rawMessage.status, rawMessage.message);
    } else if (rawMessage.type === 'conversation_restore_response') {
      // Handle conversation restoration response from backend
      console.log('Received conversation restore response:', rawMessage);
      if (rawMessage.success) {
        console.log('Conversation successfully restored on backend:', rawMessage.conversationId);
        // The backend has successfully restored the session context
        // No need to dispatch anything as the frontend state is already correct
        
        // If there's a pending message to retry, send it now
        if (pendingRetryMessageRef.current) {
          console.log('Retrying pending message after successful restoration:', pendingRetryMessageRef.current);
          wsSendMessage(JSON.stringify(pendingRetryMessageRef.current));
          pendingRetryMessageRef.current = null; // Clear after retry
        }
      } else {
        console.warn('Failed to restore conversation on backend:', rawMessage.error || 'Unknown error');
        // Could dispatch an error or notification to user if needed
        dispatch({ type: 'SET_ERROR', payload: 'Failed to restore conversation context' });
        pendingRetryMessageRef.current = null; // Clear pending message on failure
      }
    } else if (rawMessage.type === 'session_expired_restore_requested') {
      // Handle automatic restoration request from backend
      console.log('Backend requested conversation restoration:', rawMessage);
      
      // Store the original message for retry after restoration
      if (rawMessage.original_message) {
        pendingRetryMessageRef.current = rawMessage.original_message;
      }
      
      // Show user feedback about restoration
      const attemptInfo = rawMessage.attempt && rawMessage.max_attempts 
        ? ` (attempt ${rawMessage.attempt}/${rawMessage.max_attempts})`
        : '';
      console.log(`Restoring session${attemptInfo}...`);
      
      // Trigger automatic restoration with current conversation state
      if (state.conversationId && state.messages.length > 0) {
        attemptConversationRestore(state.conversationId, state.messages);
      } else {
        // No conversation data to restore - this is fine for idle/fresh sessions.
        // The new connection already has a fresh session, so just clear any error state.
        console.log('No conversation data to restore - continuing with fresh session');
        dispatch({ type: 'SET_ERROR', payload: null });
        pendingRetryMessageRef.current = null;
      }
    } else if (rawMessage.type === 'error') {
      // Handle session expired errors by auto-reconnecting instead of dead-end message
      if (rawMessage.code === 'SESSION_EXPIRED') {
        console.log('Session expired after max restoration attempts - reconnecting...');
        pendingRetryMessageRef.current = null;
        // Disconnect and reconnect to get a fresh session
        disconnectRef.current();
        setTimeout(() => connectRef.current(), 500);
        return;
      }
      dispatch({ type: 'SET_ERROR', payload: rawMessage.error });
      dispatch({ type: 'SET_PROCESSING', payload: false });
      pendingProductActionRef.current = false;
    } else if (rawMessage.type === 'moderation_blocked') {
      // Handle content moderation blocking
      console.log('Content blocked by moderation:', rawMessage);
      dispatch({ 
        type: 'MODERATION_BLOCKED', 
        payload: { 
          messageId: rawMessage.message_id, 
          content: rawMessage.content 
        }
      });
    } else if (rawMessage.type === 'product_action_response') {
      // Legacy handler for product_action_response - kept for backwards compatibility
      // New implementation uses streaming responses (type: 'stream') just like text messages
      console.log('Received legacy product action response:', rawMessage);
      
      // Call the callback if one was registered
      if (productActionCallbackRef.current) {
        productActionCallbackRef.current(rawMessage as ProductActionResponse);
        productActionCallbackRef.current = null; // Clear after use
      }
      
      dispatch({ type: 'SET_PROCESSING', payload: false });
    }  }, [state.conversationId]);  const onConnectionStatusChange = useCallback((status: 'disconnected' | 'connecting' | 'connected') => {
    dispatch({ type: 'SET_CONNECTION_STATUS', payload: status });
  }, []);

  const onError = useCallback((error: string) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  }, []);
  // WebSocket hook
  const { sendMessage: wsSendMessage, connect, disconnect } = useWebSocket(
    config.websocketUrl,
    onMessage,
    onConnectionStatusChange,
    onError
  );
  
  // Retry connection function with user-friendly error handling
  const retryConnection = useCallback(async () => {
    try {
      // Reset retry state and attempt connection
      retryHook.reset();
      const success = await retryHook.retry();
      
      if (success) {
        // If test successful, trigger actual reconnection
        connect();
      }
      
      return success;
    } catch (error) {
      console.error('Retry connection failed:', error);
      return false;
    }
  }, [retryHook, connect]);

  // Compute friendly error message
  const friendlyError = state.error ? getFriendlyError(state.error) : null;

  // Attempt to restore conversation context with the backend after reconnection
  const attemptConversationRestore = useCallback(async (conversationId: string, messages: Message[]): Promise<boolean> => {
    if (!conversationId || messages.length === 0) {
      console.log('No conversation to restore - empty conversationId or no messages');
      return false;
    }
    
    try {
      console.log('Attempting to restore conversation:', conversationId, 'with', messages.length, 'messages');
      
      // Prepare messages for backend (include all relevant fields including images)
      const messagesToRestore = messages.map(msg => ({
        id: msg.id,
        type: msg.type,
        content: msg.content,
        messageType: msg.messageType || 'text',
        timestamp: msg.timestamp.toISOString(),
        conversationId: msg.conversationId,
        // Include images for restoration if they exist
        ...(msg.images && msg.images.length > 0 && { images: msg.images })
      }));
      
      // Send restoration request to backend
      wsSendMessage(JSON.stringify({
        type: 'conversation_restore',
        conversationId: conversationId,
        messages: messagesToRestore,
        timestamp: new Date().toISOString()
      }));
      
      return true;
    } catch (error) {
      console.error('Failed to restore conversation:', error);
      return false;
    }  }, [wsSendMessage]);

  // Handle conversation restoration when connection status changes
  useEffect(() => {
    if (state.connectionStatus === 'connected' && !restorationAttemptedRef.current) {
      restorationAttemptedRef.current = true;
      
      // Check if we have conversation data to restore
      if (state.conversationId && state.messages.length > 0) {
        console.log('Connection established, attempting conversation restoration...');
        attemptConversationRestore(state.conversationId, state.messages)
          .then(success => {
            if (success) {
              console.log('Conversation restoration request sent successfully');
            } else {
              console.log('Conversation restoration not attempted (no data to restore)');
            }
          })
          .catch(error => {
            console.error('Error during conversation restoration:', error);
          });
      } else {
        console.log('No conversation data to restore');
      }
    }
    
    // Reset restoration flag when disconnected to allow restoration on next connect
    if (state.connectionStatus === 'disconnected') {
      restorationAttemptedRef.current = false;
    }
  }, [state.connectionStatus, state.conversationId, state.messages, attemptConversationRestore]);

  // Reset retry state when connection is successfully established
  useEffect(() => {
    if (state.connectionStatus === 'connected') {
      // Reset the retry hook state when connection is successful
      retryHook.reset();
      console.log('Connection established - retry state reset');
    }
  }, [state.connectionStatus, retryHook]);

  // Audio recorder hook
  const { 
    startRecording: startAudioRecording, 
    stopRecording: stopAudioRecording, 
    isRecording 
  } = useAudioRecorder(
    async (audioBlob: Blob) => {
      sendVoiceMessage(audioBlob);
    },
    (recording: boolean) => {
      dispatch({ type: 'SET_RECORDING', payload: recording });
    },
    (error: string) => {
      dispatch({ type: 'SET_ERROR', payload: error });
    },
    config.maxRecordingDuration  );  // Update recording status when it changes
  useEffect(() => {
    dispatch({ type: 'SET_RECORDING', payload: isRecording });
  }, [isRecording]);  // Load messages from sessionStorage on mount, but only after appConfig is available
  useEffect(() => {
    // Only proceed if we have the app config (indicating the app is fully initialized)
    if (!isConfigReady) {
      console.log('Delaying sessionStorage restore until app config is available');
      return;
    }
    
    try {
      // Load conversation ID
      const savedConversationId = sessionStorage.getItem(CONVERSATION_ID_KEY);
      if (savedConversationId) {
        dispatch({ type: 'SET_CONVERSATION_ID', payload: savedConversationId });
      }
      
      // Load messages
      const savedMessages = sessionStorage.getItem(STORAGE_KEY);
      if (savedMessages) {
        const messages = deserializeMessages(savedMessages);
        if (messages.length > 0) {
          console.log('Loaded', messages.length, 'messages from session storage after config became available');
          dispatch({ type: 'LOAD_MESSAGES', payload: messages });
        }
      }
      
      // If no conversation ID exists but we have messages, generate one
      if (!savedConversationId && savedMessages) {
        const newConversationId = generateConversationId();
        dispatch({ type: 'SET_CONVERSATION_ID', payload: newConversationId });
        sessionStorage.setItem(CONVERSATION_ID_KEY, newConversationId);
        console.log('Generated conversation ID for existing messages:', newConversationId);
      }
    } catch (error) {
      console.error('Failed to load conversation history from storage:', error);
    }
  }, [isConfigReady]); // Depend on isConfigReady so this runs when config becomes available

  // Save messages and conversation ID to sessionStorage when they change
  useEffect(() => {
    if (state.messages.length > 0) {
      try {
        const serialized = serializeMessages(state.messages);
        sessionStorage.setItem(STORAGE_KEY, serialized);
        console.log('Saved', state.messages.length, 'messages to session storage');
      } catch (error) {
        console.error('Failed to save conversation history to storage:', error);
        // If storage is full, try to clear and save again
        if (error instanceof Error && error.name === 'QuotaExceededError') {
          try {
            sessionStorage.removeItem(STORAGE_KEY);
            console.warn('Storage quota exceeded, cleared conversation history');
          } catch (clearError) {
            console.error('Failed to clear storage after quota error:', clearError);
          }
        }
      }
    }
  }, [state.messages]);

  // Save conversation ID when it changes
  useEffect(() => {
    if (state.conversationId) {
      try {
        sessionStorage.setItem(CONVERSATION_ID_KEY, state.conversationId);
        console.log('Saved conversation ID to session storage:', state.conversationId);
      } catch (error) {
        console.error('Failed to save conversation ID to storage:', error);
      }
    }
  }, [state.conversationId]);  const sendMessage = useCallback((content: string) => {
    // Ensure we have a conversation ID
    let currentConversationId = state.conversationId;
    if (!currentConversationId) {
      currentConversationId = generateConversationId();
      dispatch({ type: 'SET_CONVERSATION_ID', payload: currentConversationId });
      
      // Send conversation start event to backend
      wsSendMessage(JSON.stringify({
        type: 'conversation_start',
        conversationId: currentConversationId,
        timestamp: new Date().toISOString()
      }));
    }
    
    const message: Message = {
      id: Date.now().toString(),
      content,
      type: 'user',
      timestamp: new Date(),
      messageType: 'text',
      conversationId: currentConversationId
    };
    
    dispatch({ type: 'ADD_MESSAGE', payload: message });
    dispatch({ type: 'SET_PROCESSING', payload: true });
    wsSendMessage(JSON.stringify({
      type: 'text',
      content,
      conversationId: currentConversationId
    }));
  }, [wsSendMessage, state.conversationId]);  const sendVoiceMessage = useCallback(async (audioBlob: Blob) => {
    try {
      dispatch({ type: 'SET_PROCESSING', payload: true });
      
      // Ensure we have a conversation ID
      let currentConversationId = state.conversationId;
      if (!currentConversationId) {
        currentConversationId = generateConversationId();
        dispatch({ type: 'SET_CONVERSATION_ID', payload: currentConversationId });
        
        // Send conversation start event to backend
        wsSendMessage(JSON.stringify({
          type: 'conversation_start',
          conversationId: currentConversationId,
          timestamp: new Date().toISOString()
        }));
      }
      
      const arrayBuffer = await audioBlob.arrayBuffer();
      const base64Audio = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));
        // Create a placeholder message that will be updated with transcription
      const messageId = Date.now().toString();
      const message: Message = {
        id: messageId,
        content: 'Processing audio...',
        type: 'user',
        timestamp: new Date(),
        messageType: 'audio',
        isStreaming: true, // Show as processing
        conversationId: currentConversationId
      };
      
      dispatch({ type: 'ADD_MESSAGE', payload: message });
      
      wsSendMessage(JSON.stringify({
        type: 'audio',
        data: base64Audio,
        messageId: messageId, // Send the message ID to backend
        conversationId: currentConversationId,
        config: {
          sample_rate: 16000,
          channels: 1,
          bits_per_sample: 16,
          format: 'wav'
        }
      }));
    } catch (error) {
      console.error('Error sending voice message:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to send voice message' });
      dispatch({ type: 'SET_PROCESSING', payload: false });
    }
  }, [wsSendMessage, state.conversationId]);

  const sendImageSearch = useCallback((imageData: string, caption?: string, config?: any) => {
    try {
      dispatch({ type: 'SET_PROCESSING', payload: true });
      
      // Ensure we have a conversation ID
      let currentConversationId = state.conversationId;
      if (!currentConversationId) {
        currentConversationId = generateConversationId();
        dispatch({ type: 'SET_CONVERSATION_ID', payload: currentConversationId });
        
        // Send conversation start event to backend
        wsSendMessage(JSON.stringify({
          type: 'conversation_start',
          conversationId: currentConversationId,
          timestamp: new Date().toISOString()
        }));
      }
      
      // Create a placeholder message that will be updated with keywords
      const messageId = Date.now().toString();
      const message: Message = {
        id: messageId,
        content: caption || 'Analyzing image...',
        type: 'user',
        timestamp: new Date(),
        messageType: 'image_search',
        isStreaming: true, // Show as processing
        conversationId: currentConversationId,
        imageData: imageData, // Store base64 image data
        imageCaption: caption
      };
      
      dispatch({ type: 'ADD_MESSAGE', payload: message });
      
      // Send IMAGE_SEARCH message to backend
      wsSendMessage(JSON.stringify({
        type: 'image_search',
        image: imageData,
        messageId: messageId,
        conversationId: currentConversationId,
        caption: caption,
        config: config || {
          detailLevel: 'high'
        }
      }));
    } catch (error) {
      console.error('Error sending image search:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to send image search' });
      dispatch({ type: 'SET_PROCESSING', payload: false });
    }
  }, [wsSendMessage, state.conversationId]);

  const sendTTSRequest = useCallback((content: string, messageId?: string) => {
    // If messageId is provided, we're updating an existing message
    // If not provided, we're creating a new TTS request (legacy behavior)
    if (messageId) {
      // Set TTS processing state for the specific message
      dispatch({ 
        type: 'SET_TTS_PROCESSING', 
        payload: { messageId, isProcessing: true } 
      });
      
      wsSendMessage(JSON.stringify({
        type: 'tts',
        content,
        messageId: messageId, // Pass the message ID to backend
        conversationId: state.conversationId
      }));
    } else {
      // Legacy behavior: create new user message
      // Ensure we have a conversation ID
      let currentConversationId = state.conversationId;
      if (!currentConversationId) {
        currentConversationId = generateConversationId();
        dispatch({ type: 'SET_CONVERSATION_ID', payload: currentConversationId });
        
        // Send conversation start event to backend
        wsSendMessage(JSON.stringify({
          type: 'conversation_start',
          conversationId: currentConversationId,
          timestamp: new Date().toISOString()
        }));
      }
      
      const message: Message = {
        id: Date.now().toString(),
        content,
        type: 'user',
        timestamp: new Date(),
        messageType: 'tts',
        conversationId: currentConversationId
      };
      
      dispatch({ type: 'ADD_MESSAGE', payload: message });
      dispatch({ type: 'SET_PROCESSING', payload: true });
      wsSendMessage(JSON.stringify({
        type: 'tts',
        content,
        conversationId: currentConversationId
      }));    }
  }, [wsSendMessage, state.conversationId]);
  const sendCitationClick = useCallback((citationUrl: string, citationTitle: string, citationIndex?: number, messageId?: string) => {
    // Send citation click event to backend for tracking
    if (state.conversationId) {
      wsSendMessage(JSON.stringify({
        type: 'citation_click',
        citationUrl,
        citationTitle,
        citationIndex,
        messageId,
        conversationId: state.conversationId,
        timestamp: new Date().toISOString()
      }));
    }
  }, [wsSendMessage, state.conversationId]);

  const sendCopyClick = useCallback((copiedContent: string, messageId?: string) => {
    // Send copy click event to backend for tracking
    if (state.conversationId) {
      wsSendMessage(JSON.stringify({
        type: 'copy_click',
        copiedContent: copiedContent ? copiedContent.substring(0, 100) + "..." : "", // First 100 chars for privacy
        copyContentLength: copiedContent ? copiedContent.length : 0,
        messageId,
        conversationId: state.conversationId,
        timestamp: new Date().toISOString()
      }));
    }
  }, [wsSendMessage, state.conversationId]);

  const sendProductActionTracking = useCallback((
    trackingEvent: 'impression' | 'button_click' | 'product_click' | 'add_to_cart' | 'quickview_open' | 'quickview_close',
    actionType: string,
    productUrl: string,
    productTitle?: string,
    messageId?: string,
    productPrice?: string,
    productPriceNumeric?: number
  ) => {
    // Send product action tracking event to backend
    if (state.conversationId) {
      wsSendMessage(JSON.stringify({
        type: 'product_action_tracking',
        trackingEvent,
        actionType,
        productUrl,
        productTitle: productTitle || '',
        messageId: messageId || '',
        productPrice: productPrice || '',
        productPriceNumeric: productPriceNumeric ?? null,
        conversationId: state.conversationId,
        timestamp: new Date().toISOString()
      }));
    }
  }, [wsSendMessage, state.conversationId]);

  const sendFeedback = useCallback((
    messageId: string,
    feedbackType: 'positive' | 'negative',
    feedbackReasons?: string[],
    feedbackComment?: string,
    flyoutBypassed?: boolean
  ) => {
    // Send feedback event to backend
    if (state.conversationId) {
      wsSendMessage(JSON.stringify({
        type: 'feedback',
        messageId,
        feedbackType,
        feedbackReasons,
        feedbackComment,
        flyoutBypassed,
        conversationId: state.conversationId,
        timestamp: new Date().toISOString()
      }));
    }
  }, [wsSendMessage, state.conversationId]);

  // Store callback for product action response
  const productActionCallbackRef = useRef<((response: ProductActionResponse) => void) | null>(null);
  const pendingProductActionRef = useRef(false);

  const sendProductAction = useCallback((
    action: ProductActionType,
    sourceProduct: ProductActionSourceProduct,
    onResponse?: (response: ProductActionResponse) => void
  ) => {
    // Store the callback to be called when response is received
    if (onResponse) {
      productActionCallbackRef.current = onResponse;
    }

    // Create user message for the product action request
    const actionLabel = action === 'outfit_suggestion' ? 'Outfit Ideas' : 
                       action === 'more_like_this' ? 'More Like This' : 'Product Action';
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      content: `🛍️ ${actionLabel} for "${sourceProduct?.product_title || 'selected product'}"`,
      type: 'user',
      timestamp: new Date(),
      messageType: 'product_action',
      conversationId: state.conversationId || undefined,
    };
    pendingProductActionRef.current = true;
    dispatch({ type: 'ADD_MESSAGE', payload: userMessage });
    dispatch({ type: 'SET_PROCESSING', payload: true });

    // Send product action request to backend
    wsSendMessage(JSON.stringify({
      type: 'product_action',
      action,
      source_product: sourceProduct,
      conversationId: state.conversationId,
      messageId: Date.now().toString(),
      timestamp: new Date().toISOString()
    }));
    
    console.log('Product action sent:', action, sourceProduct);
  }, [wsSendMessage, state.conversationId]);

  const startRecording = useCallback(() => {
    startAudioRecording();
  }, [startAudioRecording]);

  const stopRecording = useCallback(() => {
    stopAudioRecording();
  }, [stopAudioRecording]);  const clearMessages = useCallback(() => {
    // Send conversation end event if we have an active conversation
    if (state.conversationId) {
      wsSendMessage(JSON.stringify({
        type: 'conversation_end',
        conversationId: state.conversationId,
        timestamp: new Date().toISOString()
      }));
    }
    
    dispatch({ type: 'CLEAR_MESSAGES' });
    // Clear from sessionStorage as well
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear conversation history from storage:', error);
    }
  }, [wsSendMessage, state.conversationId]);

  const startNewConversation = useCallback(() => {
    // Send conversation end event for current conversation if exists
    if (state.conversationId) {
      wsSendMessage(JSON.stringify({
        type: 'conversation_end',
        conversationId: state.conversationId,
        timestamp: new Date().toISOString()
      }));
    }
    
    // Generate new conversation ID
    const newConversationId = generateConversationId();
    
    // Start new conversation
    dispatch({ type: 'START_NEW_CONVERSATION', payload: { conversationId: newConversationId } });
    
    // Clear from sessionStorage
    try {
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.setItem(CONVERSATION_ID_KEY, newConversationId);
    } catch (error) {
      console.error('Failed to update conversation storage:', error);
    }
    
    console.log('Started new conversation:', newConversationId);
  }, [wsSendMessage, state.conversationId]);

  const clearAutoPlay = useCallback((messageId: string) => {
    dispatch({ type: 'CLEAR_AUTOPLAY', payload: { messageId } });
  }, []);
  const setAudioPlaying = useCallback((isPlaying: boolean) => {
    dispatch({ type: 'SET_AUDIO_PLAYING', payload: isPlaying });
  }, []);

  const setCurrentlyPlayingMessage = useCallback((messageId: string | null, isPlaying: boolean) => {
    dispatch({ type: 'SET_CURRENTLY_PLAYING_MESSAGE', payload: { messageId, isPlaying } });
  }, []);

  // Create stable connect/disconnect refs to avoid dependency issues
  const connectRef = useRef(connect);
  const disconnectRef = useRef(disconnect);

  useEffect(() => {
    connectRef.current = connect;
    disconnectRef.current = disconnect;
  }, [connect, disconnect]);

  // Auto-connect on mount
  useEffect(() => {
    connectRef.current();
    
    return () => {
      disconnectRef.current();
    };
  }, []);  const value: ConversationContextType = {
    ...state,
    config,
    sendMessage,
    sendVoiceMessage,
    sendImageSearch,
    sendTTSRequest,
    sendCitationClick,
    sendCopyClick,
    sendProductActionTracking,
    sendFeedback,
    sendProductAction,
    startRecording,
    stopRecording,
    clearMessages,
    startNewConversation,
    connect,
    disconnect,
    retryConnection,
    setAudioPlaying,
    setCurrentlyPlayingMessage,
    clearAutoPlay,
    // Retry state
    isRetrying: retryHook.isRetrying,
    retryAttemptCount: retryHook.attemptCount,
    nextRetryIn: retryHook.nextRetryIn,
    canRetry: retryHook.canRetry,
    // Error handling
    friendlyError,
  };

  return (
    <ConversationContext.Provider value={value}>
      {children}
    </ConversationContext.Provider>
  );
};
